'use client';

import { useState } from 'react';
import { ContactService } from '../services/contactService';
import type { ContactInquiryBase, ContactInquiry, ContactInquiryStatus } from '../interfaces/contact';
import type { ApiResponse } from '../types';

/**
 * Hook for using the ContactService in React components
 * @returns Object with contact service methods and loading state
 */
export function useContactService() {
  const [isLoading, setIsLoading] = useState(false);
  
  /**
   * Submit a new contact inquiry
   */
  const submitInquiry = async (data: ContactInquiryBase): Promise<ApiResponse<ContactInquiry>> => {
    setIsLoading(true);
    
    try {
      const result = await ContactService.submitInquiry(data);
      return result;
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Get all contact inquiries with optional filtering
   */
  const getInquiries = async (options?: { 
    limit?: number, 
    offset?: number, 
    status?: ContactInquiryStatus 
  }): Promise<ApiResponse<ContactInquiry[]>> => {
    setIsLoading(true);
    
    try {
      const result = await ContactService.getInquiries(options);
      return result;
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Get a single contact inquiry by ID
   */
  const getInquiry = async (id: number): Promise<ApiResponse<ContactInquiry>> => {
    setIsLoading(true);
    
    try {
      const result = await ContactService.getInquiry(id);
      return result;
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Update contact inquiry status and optional notes
   */
  const updateInquiryStatus = async (
    id: number, 
    status: ContactInquiryStatus, 
    notes?: string
  ): Promise<ApiResponse<ContactInquiry>> => {
    setIsLoading(true);
    
    try {
      const result = await ContactService.updateInquiryStatus(id, status, notes);
      return result;
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    isLoading,
    submitInquiry,
    getInquiries,
    getInquiry,
    updateInquiryStatus
  };
} 